import React from "react";
import {
  DollarSign,
  Calendar,
  Download,
  FileText,
  Truck,
  Package,
  ShieldCheck,
  ReceiptText,
  AlertTriangle,
  CheckCircle2
} from "lucide-react";
import { RoleHelper, DD_MM_YYYY } from "src/helpers";
import { formatFloatWithTwoDecimals } from "src/utils";
import { POAdditionalInfo, POFinanceInfo, Product, POInvoiceStatus } from "src/types";
import { useAuthorisation } from "src/context";
import { FileDownloadButton, NoteDisplay, EditPaymentTerms, EditInvoiceStatus } from "src/components";
import { CollapsibleSection } from "src/components/ui/collapsible";

interface PurchaseOrderFinanceSectionProps extends POFinanceInfo {
  invoiceNote?: POAdditionalInfo["invoiceNote"];
  downloadInvoices: () => void;
  isFileDownloading?: boolean;
  products?: Product[];
  isProForma?: boolean;
  purchaseOrderId?: string;
  onUpdateInvoiceStatus?: (status: POInvoiceStatus) => void;
  isUpdatingInvoiceStatus?: boolean;
  invoiceStatusUpdateError?: Error | null;
  paymentTerms?: string;
  onUpdatePaymentTerms?: (paymentTerms: string | null) => void;
  isUpdatingPaymentTerms?: boolean;
  paymentTermsUpdateError?: Error | null;
}

const PurchaseOrderFinanceSection: React.FC<PurchaseOrderFinanceSectionProps> = ({
  totalCost,
  brandInvoiceValue,
  brandInvoiceCurrency,
  invoiceReconciliationStatus,
  invoiceReconciliationDetail,
  hasInvoice,
  invoiceDueDate,
  invoicedAt,
  invoiceStatus,
  shipmentCost,
  importDutyCost,
  invoiceNote = null,
  downloadInvoices,
  isFileDownloading,
  products = [],
  isProForma = false,
  purchaseOrderId,
  onUpdateInvoiceStatus,
  isUpdatingInvoiceStatus = false,
  invoiceStatusUpdateError,
  paymentTerms,
  onUpdatePaymentTerms,
  isUpdatingPaymentTerms = false,
  paymentTermsUpdateError
}) => {
  const { userRole } = useAuthorisation();

  const isFinanceUser = RoleHelper.isHealfOperationsTeam(userRole) || userRole === "HEALF_ACCOUNTANT";

  const isMatch = invoiceReconciliationStatus === "MATCH";
  const isMismatch = invoiceReconciliationStatus === "MISMATCH";
  const needsReview = invoiceReconciliationStatus === "NEEDS_REVIEW";

  const invoiceReconciliationBanner = isMatch ? (
    <div className="rounded-2xl border border-success-200 bg-success-50 p-4">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0 text-success-600" />
        <div>
          <p className="text-sm font-semibold text-success-800">Invoice value validated</p>
          <p className="text-sm text-success-700">
            {invoiceReconciliationDetail ?? "The supplier invoice total matches the customs invoice total."}
          </p>
        </div>
      </div>
    </div>
  ) : isMismatch || needsReview ? (
    <div
      className={`rounded-2xl border p-4 ${isMismatch ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"}`}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${isMismatch ? "text-red-600" : "text-amber-500"}`} />
        <div>
          <p className={`text-sm font-semibold ${isMismatch ? "text-red-800" : "text-amber-800"}`}>
            {isMismatch ? "Invoice value discrepancy" : "Invoice value needs review"}
          </p>
          <p className={`text-sm ${isMismatch ? "text-red-700" : "text-amber-700"}`}>
            {invoiceReconciliationDetail ??
              (isMismatch
                ? `The supplier invoice value${
                    brandInvoiceValue != null ? ` (${brandInvoiceCurrency ?? ""}${brandInvoiceValue.toFixed(2)})` : ""
                  } does not match the customs invoice total${totalCost != null ? ` (£${totalCost.toFixed(2)})` : ""}.`
                : "We couldn't read the supplier invoice total confidently — please check it manually against the customs invoice total.")}
          </p>
        </div>
      </div>
    </div>
  ) : null;

  const formattedProducts =
    products?.map((product) => ({
      id: product.id,
      name: product.name,
      unitCost: product.unitCost
    })) ?? [];

  const limitedViewContent =
    !isFinanceUser &&
    (hasInvoice ||
      invoiceNote ||
      isProForma ||
      invoiceStatus ||
      paymentTerms !== undefined ||
      isMatch ||
      isMismatch ||
      needsReview) ? (
      <div className="space-y-5">
        {invoiceReconciliationBanner}
        <div className="rounded-2xl border border-secondary-200 bg-gradient-to-br from-white via-secondary-50 to-primary-50 p-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <ReceiptText className="h-5 w-5 text-primary-600" />
              <div>
                <p className="text-sm font-semibold text-secondary-900">Finance snapshot</p>
                <p className="text-xs text-secondary-600">Access detailed data via finance role</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {hasInvoice && (
            <div className="rounded-xl border border-secondary-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Download className="w-4 h-4 text-primary-600" />
                  <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">Invoices</span>
                </div>
                <FileDownloadButton
                  handleDownload={downloadInvoices}
                  isDisabled={isFileDownloading}
                  buttonText="Download"
                />
              </div>
            </div>
          )}
          {invoiceDueDate && (
            <div className="rounded-xl border border-secondary-200 bg-white p-4 shadow-sm">
              <div className="flex items-center space-x-2 mb-2">
                <Calendar className="w-4 h-4 text-primary-600" />
                <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
                  Invoice Due Date
                </span>
              </div>
              <p className="text-sm font-medium text-secondary-900">
                {DD_MM_YYYY.formatDate(new Date(invoiceDueDate))}
              </p>
            </div>
          )}
          {invoicedAt && (
            <div className="rounded-xl border border-secondary-200 bg-white p-4 shadow-sm">
              <div className="flex items-center space-x-2 mb-2">
                <Calendar className="w-4 h-4 text-primary-600" />
                <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">Invoiced At</span>
              </div>
              <p className="text-sm font-medium text-secondary-900">{DD_MM_YYYY.formatDate(new Date(invoicedAt))}</p>
            </div>
          )}
          <EditInvoiceStatus
            invoiceStatus={invoiceStatus}
            purchaseOrderId={purchaseOrderId}
            onUpdateInvoiceStatus={onUpdateInvoiceStatus}
            isUpdatingInvoiceStatus={isUpdatingInvoiceStatus}
            invoiceStatusUpdateError={invoiceStatusUpdateError}
            variant="limited"
          />
        </div>

        {invoiceNote && (
          <div className="rounded-2xl border border-primary-100 bg-primary-50 p-4">
            <div className="flex items-start space-x-3">
              <FileText className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-secondary-800 mb-2">Invoice Note</p>
                <div className="text-sm text-secondary-700 leading-relaxed">
                  <NoteDisplay note={invoiceNote} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    ) : null;

  const fullViewHasContent =
    totalCost !== undefined ||
    hasInvoice ||
    invoiceDueDate ||
    invoicedAt ||
    invoiceStatus ||
    shipmentCost ||
    importDutyCost ||
    invoiceNote ||
    paymentTerms !== undefined ||
    isMatch ||
    isMismatch ||
    needsReview ||
    formattedProducts.length;

  const fullViewContent =
    isFinanceUser && fullViewHasContent ? (
      <div className="space-y-6">
        {invoiceReconciliationBanner}
        <div className="grid gap-4 lg:grid-cols-3">
          {totalCost !== undefined && (
            <div
              className={`${isProForma ? "lg:col-span-2" : "lg:col-span-3"} rounded-2xl border border-success-200 bg-gradient-to-r from-success-50 via-white to-primary-50 p-6 shadow-sm`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <DollarSign className="w-6 h-6 text-success-600" />
                  <div>
                    <p className="text-sm font-semibold text-secondary-700 uppercase tracking-wide">Total Cost</p>
                  </div>
                </div>
                <p className="text-3xl font-bold text-success-700">£{formatFloatWithTwoDecimals(totalCost)}</p>
              </div>
            </div>
          )}
          {isProForma && (
            <div className="rounded-2xl border border-secondary-200 bg-white p-6 shadow-sm">
              <div className="flex items-center space-x-3">
                <ShieldCheck className="w-5 h-5 text-secondary-700" />
                <div>
                  <p className="text-sm font-semibold text-secondary-900">Invoice Type</p>
                  <p className="text-xs text-secondary-500">Current billing structure</p>
                </div>
              </div>
              <span
                className={`mt-4 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${"border border-amber-300 bg-amber-50 text-amber-800"}`}
              >
                Pro Forma Invoice
              </span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {hasInvoice && (
            <div className="rounded-2xl border border-secondary-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Download className="w-4 h-4 text-primary-600" />
                  <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">Invoices</span>
                </div>
                <FileDownloadButton
                  handleDownload={downloadInvoices}
                  isDisabled={isFileDownloading}
                  buttonText="Download"
                />
              </div>
            </div>
          )}
          {invoiceDueDate && (
            <div className="rounded-2xl border border-secondary-200 bg-white p-5 shadow-sm">
              <div className="flex items-center space-x-2 mb-1">
                <Calendar className="w-4 h-4 text-primary-600" />
                <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
                  Invoice Due Date
                </span>
              </div>
              <p className="text-lg font-semibold text-secondary-900">
                {DD_MM_YYYY.formatDate(new Date(invoiceDueDate))}
              </p>
            </div>
          )}
          {invoicedAt && (
            <div className="rounded-2xl border border-secondary-200 bg-white p-5 shadow-sm">
              <div className="flex items-center space-x-2 mb-1">
                <Calendar className="w-4 h-4 text-primary-600" />
                <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">Invoiced At</span>
              </div>
              <p className="text-lg font-semibold text-secondary-900">{DD_MM_YYYY.formatDate(new Date(invoicedAt))}</p>
            </div>
          )}
          <EditInvoiceStatus
            invoiceStatus={invoiceStatus}
            purchaseOrderId={purchaseOrderId}
            onUpdateInvoiceStatus={onUpdateInvoiceStatus}
            isUpdatingInvoiceStatus={isUpdatingInvoiceStatus}
            invoiceStatusUpdateError={invoiceStatusUpdateError}
            variant="full"
          />
          <EditPaymentTerms
            paymentTerms={paymentTerms}
            onUpdatePaymentTerms={onUpdatePaymentTerms}
            isUpdatingPaymentTerms={isUpdatingPaymentTerms}
            paymentTermsUpdateError={paymentTermsUpdateError}
          />
        </div>

        {(shipmentCost || importDutyCost) && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {shipmentCost && (
              <div className="rounded-2xl border border-secondary-200 bg-white p-5 shadow-sm">
                <div className="flex items-center space-x-2 mb-2">
                  <Truck className="w-4 h-4 text-primary-600" />
                  <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
                    Shipment Cost
                  </span>
                </div>
                <p className="text-2xl font-semibold text-secondary-900">£{formatFloatWithTwoDecimals(shipmentCost)}</p>
                <p className="text-xs text-secondary-500 mt-1">Carrier charges including handling.</p>
              </div>
            )}
            {importDutyCost && (
              <div className="rounded-2xl border border-secondary-200 bg-white p-5 shadow-sm">
                <div className="flex items-center space-x-2 mb-2">
                  <Package className="w-4 h-4 text-primary-600" />
                  <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
                    Import Duty Cost
                  </span>
                </div>
                <p className="text-2xl font-semibold text-secondary-900">
                  £{formatFloatWithTwoDecimals(importDutyCost)}
                </p>
                <p className="text-xs text-secondary-500 mt-1">Customs & HMRC duties captured at entry.</p>
              </div>
            )}
          </div>
        )}

        {invoiceNote && (
          <div className="rounded-2xl border border-primary-100 bg-primary-50 p-5 shadow-sm">
            <div className="flex items-start space-x-3">
              <FileText className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-secondary-800 mb-2">Invoice Note</p>
                <div className="text-sm text-secondary-700 leading-relaxed">
                  <NoteDisplay note={invoiceNote} />
                </div>
              </div>
            </div>
          </div>
        )}

        {formattedProducts.length > 0 && (
          <div className="rounded-2xl border border-secondary-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-secondary-900">Product Cost Breakdown</p>
                <p className="text-xs text-secondary-500">ID, name & unit cost for finance reconciliation.</p>
              </div>
              <span className="text-xs font-semibold text-secondary-500">{formattedProducts.length} products</span>
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full divide-y divide-secondary-100 text-sm">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold uppercase tracking-wide text-secondary-500">ID</th>
                    <th className="px-3 py-2 text-left font-semibold uppercase tracking-wide text-secondary-500">
                      Name
                    </th>
                    <th className="px-3 py-2 text-right font-semibold uppercase tracking-wide text-secondary-500">
                      Unit Cost
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-50">
                  {formattedProducts.map((product) => (
                    <tr key={product.id}>
                      <td className="px-3 py-2 font-mono text-xs text-secondary-700">{product.id}</td>
                      <td className="px-3 py-2 text-secondary-900">{product.name}</td>
                      <td className="px-3 py-2 text-right font-semibold text-secondary-900">
                        £{formatFloatWithTwoDecimals(product.unitCost)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    ) : null;

  if (!limitedViewContent && !fullViewContent) {
    return null;
  }

  return (
    <div className="bg-white border border-secondary-200 rounded-2xl shadow-soft overflow-hidden">
      <CollapsibleSection
        initiallyOpen={false}
        title={
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center">
              <div className="mr-3 rounded-full bg-primary-50 p-2">
                <DollarSign className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-secondary-500">Finance</p>
                <h3 className="text-lg font-semibold text-secondary-800">Invoicing Overview</h3>
              </div>
            </div>
            {isProForma && (
              <span className="inline-flex items-center rounded-full ml-3 px-3 py-1 text-xs font-semibold uppercase tracking-wide border border-amber-300 bg-amber-50 text-amber-800">
                Pro Forma
              </span>
            )}
          </div>
        }
      >
        <div className="p-6 bg-secondary-50">{isFinanceUser ? fullViewContent : limitedViewContent}</div>
      </CollapsibleSection>
    </div>
  );
};

export { PurchaseOrderFinanceSection };
